import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Phone, MessageCircle, Eye, ArrowLeft, UserPlus, Calendar } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { StatusBadge } from "../components/common/StatusBadge";
import { QuickActions } from "../components/common/QuickActions";
import { CallLogModal } from "../components/common/CallLogModal";
import { useAuth } from "../context/AuthContext";
import { apiRequest, formatPhone, formatDate, formatDateTime } from "../lib/utils";
import { toast } from "sonner";

export const LeadsFollowUpPage = () => {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [callLogContact, setCallLogContact] = useState(null);

  useEffect(() => {
    fetchLeadsFollowUps();
  }, [user?.id]);

  const fetchLeadsFollowUps = async () => {
    try {
      // For CRE users, use cre_user_id to filter by their assigned branches
      const creParam = !isAdmin() && user?.id ? `?cre_user_id=${user.id}` : "";
      const data = await apiRequest(`/dashboard/leads-follow-ups${creParam}`);
      setLeads(data || []);
    } catch (error) {
      console.error("Failed to fetch leads follow-ups:", error);
      toast.error("Failed to load follow-ups");
    } finally {
      setLoading(false);
    }
  };

  const handleCall = (lead, skipDialer = false) => {
    // First open the phone dialer (unless skipped, e.g., from QuickActions which handles it)
    if (!skipDialer && lead.phone) {
      window.location.href = `tel:${lead.phone}`;
    }
    // Show call log modal after a brief delay to allow dialer to open
    setTimeout(() => {
      setCallLogContact({
        id: lead.id,
        name: lead.name,
        phone: lead.phone,
        status: lead.status,
        type: "lead",
        branch_id: lead.branch_id
      });
    }, skipDialer ? 0 : 800);
  };

  return (
    <div className="space-y-6" data-testid="leads-follow-up-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button 
          variant="ghost" 
          size="icon" 
          className="md:hidden"
          onClick={() => navigate("/calls")}
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-purple-100">
            <UserPlus className="w-6 h-6 text-purple-600" />
          </div>
          <div>
            <h1 className="font-heading text-2xl font-bold">Leads Follow-up</h1>
            <p className="text-muted-foreground text-sm">
              {leads.length} leads from Meta Ads / Field Marketing
            </p>
          </div>
        </div>
      </div>

      {/* Desktop Table */}
      <Card className="bento-card hidden md:block">
        <CardContent className="p-0">
          <div className="data-table-container">
            <Table>
              <TableHeader>
                <TableRow className="table-high-density">
                  <TableHead>Name</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Next Follow-up</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8">Loading...</TableCell>
                  </TableRow>
                ) : leads.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      No leads follow-ups scheduled
                    </TableCell>
                  </TableRow>
                ) : (
                  leads.map((lead) => (
                    <TableRow key={lead.id} className="table-high-density" data-testid={`lead-row-${lead.id}`}>
                      <TableCell className="font-medium">{lead.name}</TableCell>
                      <TableCell className="font-mono text-sm">{formatPhone(lead.phone)}</TableCell>
                      <TableCell>
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {lead.source === "meta_ads" ? "Meta Ads" : "Field Marketing"}
                        </span>
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={lead.status} />
                      </TableCell>
                      <TableCell className="text-sm">
                        {lead.next_follow_up ? formatDateTime(lead.next_follow_up) : "-"}
                      </TableCell>
                      <TableCell className="text-right">
                        <QuickActions
                          phone={lead.phone}
                          onCall={() => handleCall(lead, true)}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-3">
        {loading ? (
          <div className="text-center py-8 text-muted-foreground">Loading...</div>
        ) : leads.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No leads follow-ups scheduled
          </div>
        ) : (
          leads.map((lead) => (
            <Card key={lead.id} className="bento-card" data-testid={`lead-card-${lead.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{lead.name}</p>
                    <p className="text-sm text-muted-foreground font-mono">{formatPhone(lead.phone)}</p>
                  </div>
                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {lead.source === "meta_ads" ? "Meta" : "Field"}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground mb-3">
                  <Calendar className="w-3 h-3" />
                  <span>Follow-up: {lead.next_follow_up ? formatDateTime(lead.next_follow_up) : "Not scheduled"}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1 text-green-600 border-green-200 hover:bg-green-50"
                    onClick={() => handleCall(lead)}
                  >
                    <Phone className="w-4 h-4 mr-1" />
                    Call
                  </Button>
                  <a
                    href={`https://wa.me/91${lead.phone?.replace(/\D/g, "")}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1"
                  >
                    <Button size="sm" variant="outline" className="w-full text-green-600 border-green-200 hover:bg-green-50">
                      <MessageCircle className="w-4 h-4 mr-1" />
                      WA
                    </Button>
                  </a>
                  <Button size="sm" variant="outline" onClick={() => navigate(`/leads/${lead.id}`)}>
                    <Eye className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Call Log Modal */}
      {callLogContact && (
        <CallLogModal
          open={!!callLogContact}
          onClose={() => setCallLogContact(null)}
          contact={callLogContact}
          onSuccess={() => {
            setCallLogContact(null);
            fetchLeadsFollowUps();
          }}
        />
      )}
    </div>
  );
};

export default LeadsFollowUpPage;
